import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Tag, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PRICING, formatPriceRange } from '../data/pricing';
import SEO from '../components/SEO';

const categoryIcons = {
  residential: '🏠',
  commercial: '🏢',
  specialty: '🔧',
};

const typeLabels = {
  hourly: { label: 'Hourly', className: 'bg-sky-100 text-sky-700' },
  fixed: { label: 'Fixed Price', className: 'bg-emerald-100 text-emerald-700' },
  addon: { label: 'Add-On', className: 'bg-amber-100 text-amber-700' },
  custom: { label: 'Custom Quote', className: 'bg-purple-100 text-purple-700' },
};

const Packages = () => {
  return (
    <div>
      <SEO
        title="Pricing"
        description="Transparent pricing for all Serene Sparkle cleaning services. Residential from £18/hr, deep cleaning from £120, end of tenancy from £150."
        keywords="cleaning prices London, house cleaning cost, office cleaning rates, deep cleaning price"
        canonical="/packages"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Our Pricing</h1>
            <p className="text-base sm:text-lg text-slate-600">
              Transparent, competitive pricing for all our cleaning services. Every quote is tailored to your specific needs.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Tables */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="space-y-12">
            {Object.entries(PRICING).map(([key, category]) => (
              <div key={key} data-testid={`pricing-${key}`}>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-3xl">{categoryIcons[key]}</span>
                  <h2 className="text-2xl font-bold text-slate-900">{category.label}</h2>
                </div>

                <div className="grid gap-4">
                  {category.services.map((service) => {
                    const typeBadge = typeLabels[service.type];
                    return (
                      <Card key={service.id} className="border-slate-200 hover:border-sky-200 hover:shadow-md transition-all" data-testid={`service-card-${service.id}`}>
                        <CardContent className="py-5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-900">{service.name}</h3>
                                <Badge className={`text-xs ${typeBadge.className}`}>
                                  {typeBadge.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-500">{service.description}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-lg font-bold text-sky-700">{formatPriceRange(service)}</p>
                              <p className="text-xs text-slate-400">{service.unit}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-emerald-50">
              <CardContent className="py-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Ready to Get Started?</h3>
                <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                  Every space is unique. Request a free, no-obligation quote and we'll tailor a cleaning plan to your specific requirements and budget.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-sky-600 hover:bg-sky-700" data-testid="get-quote-cta">
                    Get a Free Quote <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Info Notes */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
              <Clock className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Hourly Services</p>
                <p className="text-xs text-slate-500">Minimum 2-hour booking. Rates vary based on requirements.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
              <Tag className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Fixed Pricing</p>
                <p className="text-xs text-slate-500">Final price depends on property size and condition.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
              <Sparkles className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">£15 Booking Fee</p>
                <p className="text-xs text-slate-500">Deposit deducted from your total. Remainder due on completion.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
